/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, TrendingDown, MoreVertical, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { HoldingDialog } from '@/components/crypto-holding-dialog/HoldingDialog';
import { useDeleteCryptoHolding } from '@cryptoHooks';
import {CryptoHolding, CryptoWallet} from "@/integrations/supabase/crypto.models.ts";

interface CryptoHoldingListProps {
  holdings: (CryptoHolding & { wallet?: any })[];
  aggregatedHoldings: AggregatedCryptoHolding[];
  prices: CryptoPrice[];
  pricesLoading?: boolean;
  baseCurrency: string;
  wallets: CryptoWallet[];
  onSelectHolding?: (holding: CryptoHolding) => void;
}

export const CryptoHoldingList: React.FC<CryptoHoldingListProps> = ({
  holdings,
  aggregatedHoldings,
  prices,
  pricesLoading = false,
  baseCurrency,
  wallets,
}) => {
  const navigate = useNavigate();
  const [editingHolding, setEditingHolding] = useState<CryptoHolding | null>(null);
  const [deletingHolding, setDeletingHolding] = useState<CryptoHolding | null>(null);
  const deleteHolding = useDeleteCryptoHolding();

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: baseCurrency }).format(value);

  const empty = holdings.length === 0;

  const handleRowClick = (holding: CryptoHolding) => {
    navigate(`/investments/crypto/${holding.id}`);
  };

  const handleConfirmDelete = async () => {
    if (!deletingHolding) return;
    try {
      await deleteHolding.mutateAsync(deletingHolding.id);
      setDeletingHolding(null);
    } catch {
      toast.error('Failed to delete holding. Please try again.');
    }
  };

  return (
    <>
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Your Holdings</h3>

      {empty ? (
        <Card className="bg-muted/50 border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-10 text-center">
            <Wallet className="h-10 w-10 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No holdings found. Add your first crypto holding to start tracking.</p>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="by-wallet">By Wallet</TabsTrigger>
          </TabsList>

          {/* ── Overview tab: one row per aggregated symbol ── */}
          <TabsContent value="overview" className="space-y-3 mt-4">
            {aggregatedHoldings.map((agg) => {
              const priceData = prices.find(p => p.symbol === agg.symbol);
              const currentPrice = priceData?.price || 0;
              const currentValue = agg.totalAmount * currentPrice;
              const profitLoss = currentValue - agg.totalCostBasis;
              const isProfit = profitLoss >= 0;
              const change24h = priceData?.change_24h || 0;

              return (
                <Card 
                  key={agg.symbol}
                  className="hover:bg-accent/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/investments/crypto/asset/${agg.symbol}`)}
                >
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {priceData?.image ? (
                        <img src={priceData.image} alt={agg.name} className="h-10 w-10 rounded-full" />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                          {agg.symbol.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold">{agg.name}</span>
                          <Badge variant="secondary" className="text-xs">
                            {agg.walletCount} wallet{agg.walletCount !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {agg.totalAmount.toLocaleString(undefined, { maximumFractionDigits: 8 })} {agg.symbol}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-8">
                      <div className="text-right hidden md:block">
                        {pricesLoading ? (
                          <>
                            <Skeleton className="h-5 w-20 mb-1" />
                            <Skeleton className="h-4 w-12" />
                          </>
                        ) : (
                          <>
                            <div className="font-medium">{formatCurrency(currentPrice)}</div>
                            <div className={`text-xs flex items-center justify-end ${change24h >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {change24h >= 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                              {Math.abs(change24h).toFixed(2)}%
                            </div>
                          </>
                        )}
                      </div>

                      <div className="text-right">
                        {pricesLoading ? (
                          <>
                            <Skeleton className="h-5 w-24 mb-1" />
                            <Skeleton className="h-4 w-16" />
                          </>
                        ) : (
                          <>
                            <div className="font-bold">{formatCurrency(currentValue)}</div>
                            <div className={`text-xs ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
                              {isProfit ? '+' : ''}{formatCurrency(profitLoss)}
                            </div>
                          </>
                        )}
                      </div>

                      <div className="text-right hidden lg:block">
                        <div className="text-xs text-muted-foreground">Cost basis</div>
                        <div className="text-sm font-medium">{formatCurrency(agg.totalCostBasis)}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>

          {/* ── By Wallet tab: existing per-wallet list ── */}
          <TabsContent value="by-wallet" className="space-y-3 mt-4">
            {holdings.map((holding) => {
              const priceData = prices.find(p => p.symbol === holding.symbol);
              const currentPrice = priceData?.price || 0;
              const currentValue = holding.amount * currentPrice;
              const profitLoss = currentValue - holding.cost_basis;
              const isProfit = profitLoss >= 0;
              const change24h = priceData?.change_24h || 0;

              return (
                <Card
                  key={holding.id}
                  className="hover:bg-accent/50 transition-colors cursor-pointer"
                  onClick={() => handleRowClick(holding)}
                >
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {priceData?.image ? (
                        <img src={priceData.image} alt={holding.name} className="h-10 w-10 rounded-full" />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                          {holding.symbol.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="font-bold">{holding.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {holding.amount} {holding.symbol} • {holding.wallet?.provider_name || 'Unknown Wallet'}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-8">
                      <div className="text-right hidden md:block">
                        {pricesLoading ? (
                          <>
                            <Skeleton className="h-5 w-20 mb-1" />
                            <Skeleton className="h-4 w-12" />
                          </>
                        ) : (
                          <>
                            <div className="font-medium">{formatCurrency(currentPrice)}</div>
                            <div className={`text-xs flex items-center justify-end ${change24h >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {change24h >= 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                              {Math.abs(change24h).toFixed(2)}%
                            </div>
                          </>
                        )}
                      </div>

                      <div className="text-right">
                        {pricesLoading ? (
                          <>
                            <Skeleton className="h-5 w-24 mb-1" />
                            <Skeleton className="h-4 w-16" />
                          </>
                        ) : (
                          <>
                            <div className="font-bold">{formatCurrency(currentValue)}</div>
                            <div className={`text-xs ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
                              {isProfit ? '+' : ''}{formatCurrency(profitLoss)}
                            </div>
                          </>
                        )}
                      </div>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleRowClick(holding); }}>
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditingHolding(holding); }}>
                            Edit Holding
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-red-600" onClick={(e) => { e.stopPropagation(); setDeletingHolding(holding); }}>
                            Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>
        </Tabs>
      )}
    </div>

    {/* Edit Holding Dialog */}
    <HoldingDialog
      open={!!editingHolding}
      onOpenChange={(open) => { if (!open) setEditingHolding(null); }}
      wallets={wallets}
      holding={editingHolding ?? undefined}
      onSuccess={() => setEditingHolding(null)}
    />

    {/* Delete Confirmation Dialog */}
    <AlertDialog open={!!deletingHolding} onOpenChange={(open) => { if (!open) setDeletingHolding(null); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Holding</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete your {deletingHolding?.name} ({deletingHolding?.symbol}) holding?
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirmDelete}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
  );
};
